import React, {memo, useCallback, useState} from 'react';
import {HibuButton} from '../hibuButton/hibuButton';
import {Icons} from '../../icons/icons';
import './ViewerToolbar.scss';
import '../../search-input-field.scss';
import classNames from 'clsx';
import {
    changeInDocumentQueryAction,
    doFetchPdfDocumentPreviews,
    resetTransformAction
} from '../../search/duck/search-actions';
import {useDispatch, useSelector} from 'react-redux';
import {DocButtonRow} from '../../search/search-results/search-preview/doc-button-section/docButtonRow';
import {
    useSelectedDocContext,
    useStateInDocumentQuery,
    useStateQuery,
    useStateShowAnnotations
} from '../../search/search-hooks';
import {SearchPageType} from '../../search/search-config';
import {SvgIcon} from '../../icons/svgIcon';
import {useTranslation} from 'react-i18next';
import {Form} from 'react-bootstrap';

interface ViewerToolbarProps {
    highlightControl?: {
        onHighlightIndexChange: (highlightIndex: number) => void;
        numHighlights: number;
        highlightedIndex: number;
    };
    onSearchTriggered?: () => void;
}

export const ViewerToolbar = memo(({highlightControl, onSearchTriggered}: ViewerToolbarProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const query = useStateQuery();
    const inDocumentQuery = useStateInDocumentQuery();
    const selectedDocument = useSelectedDocContext();
    const containerName = useSelector((state) => state.settings.containerName);
    const [isDocumentSearchOpen, setIsDocumentSearchOpen] = useState<boolean>(!!inDocumentQuery || !!query);
    const [currentValue, setCurrentValue] = useState<string>(inDocumentQuery !== undefined ? inDocumentQuery : query);
    const showAnnotations = useStateShowAnnotations();
    const spotlightTagTypes = useSelector((state) => state.config.search.spotlightTagTypes);

    const hasContent = selectedDocument?.content !== undefined && selectedDocument?.content !== '';

    const triggerSearch = useCallback(async (searchExpression: string) => {
        if (selectedDocument) {
            dispatch(doFetchPdfDocumentPreviews(selectedDocument, showAnnotations, SearchPageType.BASIC, searchExpression, searchExpression, containerName, spotlightTagTypes));
            dispatch(changeInDocumentQueryAction(searchExpression));
            if (onSearchTriggered) {
                onSearchTriggered();
            }
        }
        // dependencies stay constant, avoid unnecessary re-renders
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDocument]);


    const previousHighlight = () => {
        dispatch(resetTransformAction(true));
        if (highlightControl) {
            highlightControl.onHighlightIndexChange(highlightControl.highlightedIndex - 1);
        }
    };
    const nextHighlight = () => {
        dispatch(resetTransformAction(true));
        if (highlightControl) {
            highlightControl.onHighlightIndexChange(highlightControl.highlightedIndex + 1);
        }
    };

    const openDocumentSearch = () => {
        setIsDocumentSearchOpen(true);
    };

    const clearInput = () => {
        triggerSearch('').then(() => {
            setCurrentValue('');
        });
    };

    const renderIcon = (currentValue: string) => {
        if (currentValue === '') {
            return (
                <SvgIcon
                    className="search-bar-icon"
                    icon={Icons.SEARCH}
                    alt={t('search.label.placeholder')}
                />
            );
        } else {
            return (
                <SvgIcon
                    className="search-bar-icon clear"
                    icon={Icons.X}
                    onClick={clearInput}
                    alt={t('general.clear')}
                />
            );
        }
    };

    const renderInitialSearchIcon = () => {
        return (
            <SvgIcon
                className={classNames('search-bar-icon', hasContent ? '' : 'disabled')}
                icon={Icons.SEARCH}
                onClick={() => hasContent ? openDocumentSearch() : undefined}
                alt={t('search.label.placeholder')}
            />
        );
    };

    const renderExpandedSearchForm = () => {
        return (
            <>
                <Form className={'hibu-search-bar-form'}
                      onSubmit={(event) => {
                          event.preventDefault();
                          triggerSearch(currentValue);
                      }}
                >
                    <input
                        className={'in-document-input'}
                        type="text"
                        placeholder={t('search.label.in_document_placeholder')}
                        onChange={(e) => {
                            setCurrentValue(e.target.value);
                        }}
                        defaultValue={inDocumentQuery !== undefined ? inDocumentQuery : query}
                        value={currentValue}
                    />
                    {renderIcon(currentValue)}
                </Form>
            </>
        );
    };

    const hasQuery = useCallback(() => isDocumentSearchOpen && currentValue.length > 0, [currentValue, isDocumentSearchOpen]);
    const renderHighlightControl = () => {
        if (!highlightControl) {
            return (<div id={'preview_header'}>
                <DocButtonRow hasSearchQuery={hasQuery()}/>
            </div>);
        }
        const highlightedIndex = highlightControl.numHighlights === 0 ? 0 : highlightControl.highlightedIndex + 1;
        return (
            <div id={'preview_header'}>
                <div className="hibu-search-bar preview-header-group">
                    <div id={'highlight-controller'} className={classNames(query || inDocumentQuery ? '' : 'hidden')}>
                        <p className={'toolbar-setting-text'}>
                            {highlightedIndex} / {highlightControl.numHighlights}
                        </p>
                        <HibuButton
                            onClick={previousHighlight}
                            icon={Icons.CHEVRON_UP}
                            visible
                            disabled={highlightControl.highlightedIndex <= 0}
                        />
                        <HibuButton
                            onClick={nextHighlight}
                            icon={Icons.CHEVRON_DOWN}
                            visible
                            disabled={highlightControl.highlightedIndex >= highlightControl.numHighlights - 1}
                        />
                    </div>
                    {
                        isDocumentSearchOpen ?
                            renderExpandedSearchForm()
                            :
                            renderInitialSearchIcon()
                    }
                </div>
                {!hasContent && <span id={'document_not_searchable_message'}>{t('search.label.not_searchable')}</span>}
                <DocButtonRow hasSearchQuery={hasQuery()}/>
            </div>
        );
    };

    return (
        <div className={'react-pdf__toolbar'}>
            <div className={'react-pdf__toolbar-setting'}>
                {renderHighlightControl()}
            </div>
        </div>
    );
});
